// ============================================
// Seed Templates: SOC 2 / Information Security
// Category: soc2-infosec
// Templates: 10
// ============================================

import type { DocTemplate, QualityGate } from '../../types/docops';

// ── SOC 2 Quality Gates ──

const INFO_SEC_GATE: QualityGate = {
  requiredScore: 95,
  maxAttempts: 3,
  provisions: [
    { id: 'soc2-tsc-security', title: 'Security Trust Criteria (CC)', required: true, keywords: ['access controls', 'encryption', 'audit logging'], weight: 'critical' },
    { id: 'soc2-tsc-availability', title: 'Availability Trust Criteria (A)', required: true, keywords: ['uptime', 'disaster recovery', 'incident response'], weight: 'critical' },
    { id: 'soc2-tsc-processing', title: 'Processing Integrity (PI)', required: true, keywords: ['quality gates', 'data validation', 'error boundaries'], weight: 'critical' },
    { id: 'soc2-tsc-confidentiality', title: 'Confidentiality Trust Criteria (C)', required: true, keywords: ['AES-256', 'role-based access', 'data classification'], weight: 'critical' },
    { id: 'soc2-tsc-privacy', title: 'Privacy Trust Criteria (P)', required: true, keywords: ['consent model', 'withdrawal', 'HIPAA'], weight: 'critical' },
    { id: 'soc2-governance', title: 'Security Governance Framework', required: true, keywords: ['governance', 'policy owner', 'review cycle'], weight: 'high' },
    { id: 'soc2-three-layer', title: 'Three-Layer Compliance Model', required: true, keywords: ['platform layer', 'brand layer', 'instance layer'], weight: 'high' },
    { id: 'soc2-policy-exception', title: 'Policy Exception Process', required: true, keywords: ['exception', 'approval', 'documentation'], weight: 'medium' },
  ],
};

const ACCESS_CONTROL_GATE: QualityGate = {
  requiredScore: 95,
  maxAttempts: 3,
  provisions: [
    { id: 'ac-role-matrix', title: 'Role Definitions and Access Matrix', required: true, keywords: ['patient', 'provider', 'admin', 'owner'], weight: 'critical' },
    { id: 'ac-least-privilege', title: 'Principle of Least Privilege', required: true, keywords: ['least privilege', 'minimum necessary'], weight: 'critical' },
    { id: 'ac-mfa', title: 'Multi-Factor Authentication', required: true, keywords: ['MFA', 'multi-factor', 'two-factor'], weight: 'critical' },
    { id: 'ac-brand-isolation', title: 'Brand-Scoped Access Isolation', required: true, keywords: ['brand-scoped', 'brand isolation', 'cannot see data from'], weight: 'critical' },
    { id: 'ac-quarterly-review', title: 'Quarterly Access Review', required: true, keywords: ['quarterly', 'access review', 'last login'], weight: 'high' },
    { id: 'ac-offboarding', title: 'Offboarding and Access Revocation', required: true, keywords: ['offboarding', 'revoke', '24 hours'], weight: 'high' },
    { id: 'ac-emergency', title: 'Emergency Access', required: true, keywords: ['break-glass', 'emergency access', 'post-incident'], weight: 'medium' },
  ],
};

const CHANGE_MGMT_GATE: QualityGate = {
  requiredScore: 90,
  maxAttempts: 3,
  provisions: [
    { id: 'cm-categories', title: 'Change Categories', required: true, keywords: ['standard', 'normal', 'emergency', 'major'], weight: 'critical' },
    { id: 'cm-ci-cd', title: 'CI/CD Pipeline', required: true, keywords: ['CI/CD', 'automated', 'compile', 'test'], weight: 'critical' },
    { id: 'cm-code-review', title: 'Code Review and Approval', required: true, keywords: ['PR review', 'pull request', 'code review'], weight: 'critical' },
    { id: 'cm-rollback', title: 'Rollback Procedures', required: true, keywords: ['rollback', 'previous version', 'revert'], weight: 'high' },
    { id: 'cm-deploy-logging', title: 'Deploy Logging', required: true, keywords: ['deploy log', 'commit SHA', 'audit trail'], weight: 'high' },
    { id: 'cm-emergency', title: 'Emergency Change Process', required: true, keywords: ['emergency', 'post-deploy review', '24 hours'], weight: 'medium' },
  ],
};

const DATA_CLASS_GATE: QualityGate = {
  requiredScore: 95,
  maxAttempts: 3,
  provisions: [
    { id: 'dc-phi', title: 'PHI Classification', required: true, keywords: ['PHI', 'Protected Health Information', 'HIPAA'], weight: 'critical' },
    { id: 'dc-pii', title: 'PII Classification', required: true, keywords: ['PII', 'Personally Identifiable', 'consent'], weight: 'critical' },
    { id: 'dc-firestore-map', title: 'Firestore Collection Map', required: true, keywords: ['Firestore', 'collection', 'classification'], weight: 'critical' },
    { id: 'dc-handling', title: 'Handling Requirements by Level', required: true, keywords: ['encrypted', 'access controlled', 'restricted'], weight: 'high' },
    { id: 'dc-disposal', title: 'Data Disposal', required: true, keywords: ['disposal', 'deletion', 'retention schedule'], weight: 'high' },
    { id: 'dc-sharing', title: 'Data Sharing Requirements', required: true, keywords: ['BAA', 'third-party', 'consent required'], weight: 'high' },
  ],
};

const ENCRYPTION_GATE: QualityGate = {
  requiredScore: 90,
  maxAttempts: 3,
  provisions: [
    { id: 'enc-at-rest', title: 'Encryption at Rest', required: true, keywords: ['AES-256', 'at rest', 'Firestore'], weight: 'critical' },
    { id: 'enc-in-transit', title: 'Encryption in Transit', required: true, keywords: ['TLS 1.2', 'in transit', 'HTTPS'], weight: 'critical' },
    { id: 'enc-secrets', title: 'Secrets Management', required: true, keywords: ['Firebase Secrets Manager', 'server-side only', 'never in client'], weight: 'critical' },
    { id: 'enc-key-rotation', title: 'Key Rotation', required: true, keywords: ['rotation', 'quarterly', 'Google-managed'], weight: 'high' },
    { id: 'enc-cert', title: 'Certificate Management', required: true, keywords: ['SSL', 'certificate', 'auto-renewed'], weight: 'medium' },
  ],
};

const VENDOR_MGMT_GATE: QualityGate = {
  requiredScore: 95,
  maxAttempts: 3,
  provisions: [
    { id: 'vm-register', title: 'Vendor Register', required: true, keywords: ['vendor register', 'BAA status', 'SOC 2'], weight: 'critical' },
    { id: 'vm-assessment', title: 'Vendor Assessment Criteria', required: true, keywords: ['assessment', 'security posture', 'data handling'], weight: 'critical' },
    { id: 'vm-baa', title: 'BAA Management', required: true, keywords: ['BAA', 'Business Associate Agreement', 'PHI'], weight: 'critical' },
    { id: 'vm-onboarding', title: 'Vendor Onboarding', required: true, keywords: ['onboarding', 'legal review', 'access provisioning'], weight: 'high' },
    { id: 'vm-quarterly', title: 'Quarterly Review', required: true, keywords: ['quarterly', 'review', 'SOC 2 report'], weight: 'high' },
    { id: 'vm-risk-scoring', title: 'Vendor Risk Scoring', required: true, keywords: ['critical', 'high', 'medium', 'low'], weight: 'medium' },
  ],
};

const MONITORING_GATE: QualityGate = {
  requiredScore: 90,
  maxAttempts: 3,
  provisions: [
    { id: 'mon-matrix', title: 'Monitoring Matrix', required: true, keywords: ['uptime', 'error rate', 'alert condition'], weight: 'critical' },
    { id: 'mon-escalation', title: 'Alert Escalation', required: true, keywords: ['escalation', 'critical', 'response SLA'], weight: 'critical' },
    { id: 'mon-infrastructure', title: 'Monitoring Infrastructure', required: true, keywords: ['Cloud Monitoring', 'UptimeRobot', 'Crashlytics'], weight: 'high' },
    { id: 'mon-dashboard', title: 'Dashboard Requirements', required: true, keywords: ['dashboard', 'real-time', 'admin'], weight: 'medium' },
    { id: 'mon-monthly-review', title: 'Monthly Review', required: true, keywords: ['monthly', 'review', 'false positive'], weight: 'medium' },
  ],
};

const RISK_ASSESSMENT_GATE: QualityGate = {
  requiredScore: 95,
  maxAttempts: 3,
  provisions: [
    { id: 'ra-methodology', title: 'Risk Assessment Methodology', required: true, keywords: ['likelihood', 'impact', 'risk score'], weight: 'critical' },
    { id: 'ra-register', title: 'Risk Register', required: true, keywords: ['risk register', 'PHI breach', 'insider threat'], weight: 'critical' },
    { id: 'ra-treatment', title: 'Risk Treatment Options', required: true, keywords: ['mitigate', 'transfer', 'accept', 'avoid'], weight: 'critical' },
    { id: 'ra-acceptance', title: 'Risk Acceptance Criteria', required: true, keywords: ['acceptance criteria', 'low', 'medium', 'high', 'critical'], weight: 'high' },
    { id: 'ra-annual', title: 'Annual Assessment', required: true, keywords: ['annual', 'assessment', 'control effectiveness'], weight: 'high' },
  ],
};

const AUP_GATE: QualityGate = {
  requiredScore: 90,
  maxAttempts: 3,
  provisions: [
    { id: 'aup-prohibited', title: 'Prohibited Activities', required: true, keywords: ['prohibited', 'unauthorized', 'sharing credentials'], weight: 'critical' },
    { id: 'aup-phi-handling', title: 'PHI Handling Requirements', required: true, keywords: ['PHI', 'minimum necessary', 'authorized platform'], weight: 'critical' },
    { id: 'aup-device', title: 'Device Security', required: true, keywords: ['screen lock', 'full disk encryption', 'antivirus'], weight: 'high' },
    { id: 'aup-remote', title: 'Remote Work Security', required: true, keywords: ['VPN', 'secure Wi-Fi', 'no public Wi-Fi'], weight: 'high' },
    { id: 'aup-consequences', title: 'Consequences of Violations', required: true, keywords: ['warning', 'suspension', 'termination'], weight: 'high' },
    { id: 'aup-incident-report', title: 'Incident Reporting', required: true, keywords: ['report', '4 hours', 'security incident'], weight: 'medium' },
  ],
};

const SOC2_EVIDENCE_GATE: QualityGate = {
  requiredScore: 95,
  maxAttempts: 3,
  provisions: [
    { id: 'ev-cc-security', title: 'Security Criteria (CC1-CC9)', required: true, keywords: ['CC1', 'CC6', 'CC8', 'access controls'], weight: 'critical' },
    { id: 'ev-availability', title: 'Availability Criteria (A1)', required: true, keywords: ['uptime', 'RPO', 'RTO', 'disaster recovery'], weight: 'critical' },
    { id: 'ev-processing', title: 'Processing Integrity (PI1)', required: true, keywords: ['quality gates', 'validation', 'error handling'], weight: 'critical' },
    { id: 'ev-confidentiality', title: 'Confidentiality Criteria (C1)', required: true, keywords: ['data classification', 'disposal', 'retention'], weight: 'critical' },
    { id: 'ev-privacy', title: 'Privacy Criteria (P1-P8)', required: true, keywords: ['consent', 'access rights', 'privacy notice'], weight: 'critical' },
    { id: 'ev-evidence-schedule', title: 'Evidence Collection Schedule', required: true, keywords: ['monthly', 'quarterly', 'annually', 'evidence'], weight: 'high' },
    { id: 'ev-timeline', title: 'Pre-Audit Timeline', required: true, keywords: ['timeline', 'milestone', 'audit engagement'], weight: 'high' },
  ],
};

export const SEED_TEMPLATES_SOC2: DocTemplate[] = [
  // ── 13.1 Information Security Policy ──
  {
    id: '13.1-information-security-policy',
    categoryId: 'soc2-infosec',
    title: 'Information Security Policy',
    description:
      'Master information security policy that maps to all five SOC 2 Trust Service Criteria (Security, Availability, Processing Integrity, Confidentiality, Privacy). Ties together all subordinate security policies and establishes the security governance framework.',
    templateContent: `# Information Security Policy

**{{brandEntity}}**

Effective Date: {{effectiveDate}}
Policy Owner: {{ceoName}} (CEO)
Review Cycle: Annual

## 1. Purpose and Scope
{{AI_GENERATED_CONTENT}}

## 2. SOC 2 Trust Service Criteria Mapping

| Trust Criteria | Status | Controls | Subordinate Policy |
|----------------|--------|----------|-------------------|
| Security (CC) | Required | Access controls, encryption, audit logging, intrusion detection | Access Control Policy, Encryption Policy |
| Availability (A) | Implemented | Uptime SLA, monitoring, incident response, DR plan | Monitoring Policy, BC/DR Plan |
| Processing Integrity (PI) | Implemented | Quality gates, retry logic, error boundaries, data validation | Change Management Policy |
| Confidentiality (C) | Implemented | AES-256, role-based access, PHI isolation, data classification | Data Classification Policy, Encryption Policy |
| Privacy (P) | Implemented | 7-purpose consent model, withdrawal mechanics, re-consent gates, HIPAA NPP | Consent Management Policy, Privacy Policy |

## 3. Security Governance Framework
{{AI_GENERATED_CONTENT}}

## 4. Platform Security Architecture
{{AI_GENERATED_CONTENT}}

## 5. Three-Layer Compliance Model
{{AI_GENERATED_CONTENT}}

## 6. Risk Management Overview
{{AI_GENERATED_CONTENT}}

## 7. Incident Response Overview
{{AI_GENERATED_CONTENT}}

## 8. Compliance Obligations
{{AI_GENERATED_CONTENT}}

## 9. Policy Exception Process
{{AI_GENERATED_CONTENT}}

## 10. Policy Review and Update Schedule
{{AI_GENERATED_CONTENT}}

Approved by: {{ceoName}}, CEO
Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'effectiveDate',
      'complianceEmail',
    ],
    generationPrompt:
      `Generate a master Information Security Policy for {{brandEntity}} that maps to all five SOC 2 Trust Service Criteria. This is the top-level security policy that all subordinate policies reference.

Platform architecture: Firebase Hosting + Firestore + Cloud Functions + Firebase Auth. AI services via Claude API (proxied through Cloud Functions — API keys never in client). Payment via Stripe. Telehealth via {{telehealthNetwork}}.

Three-Layer Compliance Model:
- Platform Layer: Shared infrastructure, security controls, audit system, AI engines, quality gates (inherited by all brands, never overridden)
- Brand Layer: Entity info, branding, document templates, formulary config, consent purposes (configurable per brand)
- Instance Layer: Patient data, session state, consent records, order history, audit trail (per-patient, per-brand)

Key principle: Platform controls are inherited, never overridden. A brand cannot disable audit logging, weaken access controls, bypass quality gates, or skip consent flows.

Security controls already in place:
- Firebase Auth with role-based access (patient / provider / admin / owner)
- Firestore security rules enforce row-level, brand-scoped access
- Cloud Function proxy — all API keys server-side only
- AES-256 encryption at rest (Firestore), TLS 1.2+ in transit
- Firebase Secrets Manager for API keys
- PHI never stored in browser/client
- 7-purpose granular consent model with withdrawal mechanics
- Immutable audit trail (no role can update or delete audit entries)
- Quality gate scoring on all AI-generated documents
- Error boundaries and retry logic with backoff

Subordinate policies (reference by name): Access Control Policy, Change Management Policy, Data Classification Policy, Encryption & Key Management Policy, Vendor Management Policy, Monitoring & Alerting Policy, Risk Assessment Policy, Acceptable Use Policy.

Compliance obligations: HIPAA (covered entity via PC structure), SOC 2 Type II (target), state privacy laws (FL, WA, CT, CA), MHMDA, PCI DSS (via Stripe).

Format: comprehensive master security policy suitable for SOC 2 auditor review. Include policy exception process and annual review schedule.`,
    qualityGate: INFO_SEC_GATE,
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 12,
    sortOrder: 1,
    generationRound: 7,
    dependencies: ['8.2-breach-response-plan', '8.8-business-continuity-dr'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 13.2 Access Control Policy ──
  {
    id: '13.2-access-control-policy',
    categoryId: 'soc2-infosec',
    title: 'Access Control Policy',
    description:
      'Defines role-based access control model, principle of least privilege, MFA requirements, access review schedule, and offboarding procedures. Maps to SOC 2 CC6.1-CC6.3.',
    templateContent: `# Access Control Policy

**{{brandEntity}}**

Effective Date: {{effectiveDate}}
Policy Owner: {{ceoName}} (CEO)
Review Cycle: Quarterly

## 1. Purpose and Scope
{{AI_GENERATED_CONTENT}}

## 2. Role Definitions and Access Matrix

| Role | Scope | Firebase Auth | Firestore Access | Admin Dashboard | Cloud Functions |
|------|-------|---------------|------------------|-----------------|-----------------|
| Patient | Own brand only | Email/password + optional MFA | Own records only | None | Read-only (own data) |
| Provider | Assigned brand | Email/password + required MFA | Assigned patients | Clinical views | Read (patient data) |
| Admin | Assigned brand(s) | Email/password + required MFA | Brand-scoped | Full admin | Read/write |
| Owner | All brands | Email/password + required MFA | All brands | Full admin + audit | Full access |
| System | Platform | Service account | Audit log writes | N/A | All functions |

## 3. Principle of Least Privilege
{{AI_GENERATED_CONTENT}}

## 4. Multi-Factor Authentication Requirements
{{AI_GENERATED_CONTENT}}

## 5. Access Provisioning and Approval
{{AI_GENERATED_CONTENT}}

## 6. Quarterly Access Review Process
{{AI_GENERATED_CONTENT}}

## 7. Offboarding and Access Revocation
{{AI_GENERATED_CONTENT}}

## 8. Brand-Scoped Access Isolation
{{AI_GENERATED_CONTENT}}

## 9. Emergency Access Procedures
{{AI_GENERATED_CONTENT}}

## 10. Audit Trail for Access Events
{{AI_GENERATED_CONTENT}}

Approved by: {{ceoName}}, CEO
Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'effectiveDate',
      'complianceEmail',
    ],
    generationPrompt:
      `Generate an Access Control Policy for {{brandEntity}} that maps to SOC 2 CC6.1-CC6.3.

Role model (brand-scoped):
- Patient: own brand only, email/password auth, optional MFA, Firestore access limited to own records, no admin dashboard
- Provider: assigned brand, email/password + required MFA, access to assigned patients, clinical dashboard views
- Admin: assigned brand(s), email/password + required MFA, brand-scoped Firestore access, full admin dashboard
- Owner: all brands, email/password + required MFA, all Firestore access, full admin + audit log visibility
- System: platform-level service account, audit log writes only, immutable (no update/delete)

Access control enforcement:
- Firebase Auth handles authentication with role claims
- Firestore security rules enforce brand-scoped, row-level access
- Cloud Function proxy ensures API keys never reach client
- Brand isolation: a patient in Brand A cannot see data from Brand B

MFA requirements: Required for admin, provider, and owner roles. Optional for patients. Firebase Auth native MFA.

Quarterly access review: Admin page lists all users by role with last login date. Review quarterly. Document reviewer, date, findings, actions taken.

Offboarding: Reference Employee Termination Checklist (7.8). Revoke all system access within 24 hours. Firestore rules, GitHub, admin dashboard, email, Slack. Log revocation in audit trail.

Emergency access: Break-glass procedure for system emergencies. Requires post-incident documentation and audit review.

Audit logging: All auth events logged — AUTH_LOGIN, AUTH_LOGOUT, AUTH_FAILED, AUTH_MFA_CHALLENGE, AUTH_PASSWORD_RESET, AUTH_ROLE_CHANGE. Immutable audit trail.

Format: formal policy document suitable for SOC 2 auditor review.`,
    qualityGate: ACCESS_CONTROL_GATE,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 8,
    sortOrder: 2,
    generationRound: 7,
    dependencies: ['7.8-termination-checklist'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 13.3 Change Management Policy ──
  {
    id: '13.3-change-management-policy',
    categoryId: 'soc2-infosec',
    title: 'Change Management Policy',
    description:
      'Defines how code changes are developed, reviewed, tested, approved, and deployed. Covers CI/CD pipeline, staging environments, PR review gates, rollback procedures, and deploy logging. Maps to SOC 2 CC8.1.',
    templateContent: `# Change Management Policy

**{{brandEntity}}**

Effective Date: {{effectiveDate}}
Policy Owner: {{ceoName}} (CEO)
Review Cycle: Annual

## 1. Purpose and Scope
{{AI_GENERATED_CONTENT}}

## 2. Change Categories

| Category | Examples | Approval | Testing | Deploy Window |
|----------|----------|----------|---------|---------------|
| Standard | Bug fix, UI tweak, copy change | Self-review + CI pass | Automated (CI) | Anytime |
| Normal | New feature, API change, dependency update | PR review + CI pass | Automated + staging verify | Business hours |
| Emergency | Security patch, production outage fix | Post-deploy review | Minimal viable | Immediate |
| Major | Architecture change, migration, new service | Team review + owner approval | Full regression + staging | Scheduled |

## 3. Development Workflow
{{AI_GENERATED_CONTENT}}

## 4. CI/CD Pipeline
{{AI_GENERATED_CONTENT}}

## 5. Staging Environment
{{AI_GENERATED_CONTENT}}

## 6. Code Review and Approval
{{AI_GENERATED_CONTENT}}

## 7. Deployment Process
{{AI_GENERATED_CONTENT}}

## 8. Rollback Procedures
{{AI_GENERATED_CONTENT}}

## 9. Deploy Logging and Audit Trail
{{AI_GENERATED_CONTENT}}

## 10. Emergency Change Process
{{AI_GENERATED_CONTENT}}

Approved by: {{ceoName}}, CEO
Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'effectiveDate',
      'complianceEmail',
    ],
    generationPrompt:
      `Generate a Change Management Policy for {{brandEntity}} that maps to SOC 2 CC8.1.

Current technology stack: React + Vite + TypeScript frontend, Firebase Hosting, Cloud Functions (Node.js), Firestore, Firebase Auth.

CI/CD pipeline (GitHub Actions):
- On PR: TypeScript compile (tsc --noEmit) + ESLint + Vitest tests
- Blocks merge on any failure
- On merge to main: auto-deploy to staging
- Staging verification required before production promote

Environments:
- Development: local (Vite dev server + Firebase emulators)
- Staging: hemologix-staging Firebase project
- Production: gen-lang-client-0571783648 Firebase project (hemologix.ai)

Development workflow:
1. Branch per feature/fix (never commit directly to main)
2. Develop and test locally
3. Push branch, create PR
4. CI runs automatically (compile + lint + test)
5. PR review (when team available; self-review with checklist until then)
6. Merge to main → auto-deploy to staging
7. Verify on staging
8. Promote to production: firebase deploy --only hosting --force

Rollback: firebase hosting:clone for instant rollback to previous version. Document: who, when, why, what commit was rolled back.

Deploy logging: GitHub Actions captures every deploy — who triggered, when, what commit SHA, which environment. This is the automatic audit trail.

Emergency changes: Security patches or production outage fixes can skip staging. Require post-deploy review within 24 hours with documentation of what changed, why it was urgent, and what testing was done.

Audit trail: SYS_DEPLOY events logged to audit_logs collection with version, commit SHA, deployer, environment.

Change categories: Standard (self-review), Normal (PR review + CI), Emergency (post-deploy review), Major (team review + owner approval).

Format: formal policy document suitable for SOC 2 auditor review.`,
    qualityGate: CHANGE_MGMT_GATE,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 8,
    sortOrder: 3,
    generationRound: 7,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 13.4 Data Classification Policy ──
  {
    id: '13.4-data-classification-policy',
    categoryId: 'soc2-infosec',
    title: 'Data Classification Policy',
    description:
      'Classifies all data handled by the platform into PHI, PII, Business Confidential, and Public categories. Maps each Firestore collection to a classification level with handling requirements. Maps to SOC 2 CC6.1, C1.1.',
    templateContent: `# Data Classification Policy

**{{brandEntity}}**

Effective Date: {{effectiveDate}}
Policy Owner: {{ceoName}} (CEO)
Review Cycle: Annual

## 1. Purpose and Scope
{{AI_GENERATED_CONTENT}}

## 2. Classification Levels

| Level | Label | Description | Examples | Handling |
|-------|-------|-------------|----------|----------|
| 1 | PHI (Highest) | Protected Health Information under HIPAA | Lab results, diagnoses, prescriptions, clinical notes | Encrypted at rest + transit, access logged, BAA required for sharing, 6-year retention |
| 2 | PII | Personally Identifiable Information | Name, email, phone, address, DOB, payment info | Encrypted, access controlled, consent required, retention per policy |
| 3 | Business Confidential | Internal business data | Revenue, contracts, API keys, brand configs, source code | Access restricted to authorized personnel, NDA required |
| 4 | Public | Publicly available information | Marketing content, published policies, pricing | No special handling required |

## 3. Firestore Collection Classification Map
{{AI_GENERATED_CONTENT}}

## 4. Data Handling Requirements by Level
{{AI_GENERATED_CONTENT}}

## 5. Data in Transit
{{AI_GENERATED_CONTENT}}

## 6. Data at Rest
{{AI_GENERATED_CONTENT}}

## 7. Data Sharing and Third-Party Access
{{AI_GENERATED_CONTENT}}

## 8. Data Retention by Classification
{{AI_GENERATED_CONTENT}}

## 9. Data Disposal
{{AI_GENERATED_CONTENT}}

## 10. Classification Review Process
{{AI_GENERATED_CONTENT}}

Approved by: {{ceoName}}, CEO
Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'effectiveDate',
      'complianceEmail',
    ],
    generationPrompt:
      `Generate a Data Classification Policy for {{brandEntity}} that maps to SOC 2 CC6.1 and C1.1.

Classification levels:
- Level 1 — PHI (Highest): Protected Health Information under HIPAA. Lab results, diagnoses, prescriptions, clinical notes, AI health analysis, protocol recommendations. Encrypted at rest (AES-256) + transit (TLS 1.2+), access logged to audit trail, BAA required for any third-party sharing, 6-year minimum retention per HIPAA.
- Level 2 — PII: Personally Identifiable Information. Name, email, phone, address, DOB, payment card data. Encrypted, access controlled by Firestore rules, consent required for collection, retention per data retention policy.
- Level 3 — Business Confidential: Internal business data. Revenue figures, contracts, API keys, brand configurations, source code, quality gate rubrics, AI prompts. Access restricted to authorized admin/owner roles, NDA required for contractors.
- Level 4 — Public: Publicly available information. Marketing content, published policies, pricing pages, blog posts. No special handling required.

Firestore collection map (generate complete table):
- brands/{brandId}/patients/{patientId}: Level 1 (PHI)
- brands/{brandId}/patients/{patientId}/labResults: Level 1 (PHI)
- brands/{brandId}/patients/{patientId}/protocols: Level 1 (PHI)
- brands/{brandId}/patients/{patientId}/orders: Level 2 (PII) + Level 1 (PHI for prescription orders)
- brands/{brandId}/patients/{patientId}/consents: Level 2 (PII)
- brands/{brandId}/documents/{docId}: Level 3 (Business Confidential)
- brands/{brandId}/context: Level 3 (Business Confidential)
- audit_logs/{logId}: Level 3 (Business Confidential) — may contain Level 1 references in details
- users/{userId}: Level 2 (PII)

Third-party data sharing by classification:
- PHI: Only to BAA-covered entities ({{cloudProvider}}, {{telehealthNetwork}}, pharmacy partner)
- PII: Only with consent, to processors ({{paymentProcessor}})
- Business Confidential: Only to authorized personnel under NDA
- Public: Unrestricted

Data disposal: PHI overwritten/deleted per retention schedule. PII anonymized or deleted. Business Confidential securely deleted. Disposal logged to audit trail.

Format: formal policy document suitable for SOC 2 auditor review. Include the complete Firestore collection classification table.`,
    qualityGate: DATA_CLASS_GATE,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 8,
    sortOrder: 4,
    generationRound: 7,
    dependencies: ['8.3-data-retention-policy', '8.5-data-sharing-policy'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 13.5 Encryption & Key Management Policy ──
  {
    id: '13.5-encryption-key-management-policy',
    categoryId: 'soc2-infosec',
    title: 'Encryption & Key Management Policy',
    description:
      'Defines encryption standards for data at rest and in transit, key management responsibilities, rotation schedules, certificate management, and backup encryption verification. Maps to SOC 2 CC6.1, CC6.7.',
    templateContent: `# Encryption & Key Management Policy

**{{brandEntity}}**

Effective Date: {{effectiveDate}}
Policy Owner: {{ceoName}} (CEO)
Review Cycle: Annual

## 1. Purpose and Scope
{{AI_GENERATED_CONTENT}}

## 2. Encryption Standards

| Context | Standard | Implementation | Managed By |
|---------|----------|---------------|------------|
| Data at rest (Firestore) | AES-256 | Google-managed keys, automatic | {{cloudProvider}} |
| Data in transit | TLS 1.2+ | Enforced on all Firebase connections | {{cloudProvider}} |
| SSL certificates (Hosting) | RSA 2048+ | Auto-provisioned, auto-renewed | Firebase Hosting |
| API keys and secrets | Firebase Secrets Manager | Server-side only, never in client | Platform team |
| Backup encryption | AES-256 | Firestore automatic backups | {{cloudProvider}} |

## 3. Encryption at Rest
{{AI_GENERATED_CONTENT}}

## 4. Encryption in Transit
{{AI_GENERATED_CONTENT}}

## 5. Key Management
{{AI_GENERATED_CONTENT}}

## 6. Key Rotation Schedule
{{AI_GENERATED_CONTENT}}

## 7. Certificate Management
{{AI_GENERATED_CONTENT}}

## 8. Secrets Management
{{AI_GENERATED_CONTENT}}

## 9. Backup Encryption Verification
{{AI_GENERATED_CONTENT}}

## 10. Quarterly Verification Checklist
{{AI_GENERATED_CONTENT}}

Approved by: {{ceoName}}, CEO
Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'effectiveDate',
      'cloudProvider',
      'complianceEmail',
    ],
    generationPrompt:
      `Generate an Encryption & Key Management Policy for {{brandEntity}} that maps to SOC 2 CC6.1 and CC6.7.

Current encryption infrastructure (provided by {{cloudProvider}}):
- AES-256 encryption at rest: All Firestore data automatically encrypted. Google-managed keys. Zero configuration required.
- TLS 1.2+ encryption in transit: All Firebase connections enforced by default. No unencrypted communication possible.
- SSL certificates on Firebase Hosting: Auto-provisioned, auto-renewed by Firebase. Free. Custom domain (hemologix.ai) has valid SSL.
- Firebase Secrets Manager: API keys (Anthropic, Gemini, Stripe) stored as Firebase secrets. Never in client code or browser. Accessed only by Cloud Functions server-side.
- Backup encryption: Firestore automatic backups encrypted by default with AES-256.

Key management:
- Google Cloud manages encryption key rotation automatically
- Platform team responsibility: verify key management via GCP console quarterly
- Document: "Google manages key rotation. We verify via GCP console quarterly."

Secrets management:
- ANTHROPIC_API_KEY: Firebase defineSecret, accessed only by Cloud Function proxy
- GEMINI_API_KEY: Firebase defineSecret, accessed only by geminiProxy Cloud Function (moved from client-side)
- Stripe keys: Firebase secrets, webhook signing secrets rotated per Stripe guidance
- All secrets trimmed on read (trailing newline issue documented)

Certificate management:
- Firebase Hosting auto-provisions and auto-renews SSL certificates
- Custom domain hemologix.ai: A record → 199.36.158.100, SSL auto-managed
- No manual certificate management required

Quarterly verification checklist:
1. Verify GCP encryption key rotation status in console
2. Verify SSL certificate validity for all domains
3. Verify Firebase secrets are current and not expired
4. Verify backup encryption status
5. Document verification date, reviewer, findings

Format: formal policy document suitable for SOC 2 auditor review.`,
    qualityGate: ENCRYPTION_GATE,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 6,
    sortOrder: 5,
    generationRound: 7,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 13.6 Vendor Management Policy & Register ──
  {
    id: '13.6-vendor-management-policy',
    categoryId: 'soc2-infosec',
    title: 'Vendor Management Policy & Register',
    description:
      'Defines vendor assessment criteria, onboarding process, ongoing monitoring, BAA tracking, and quarterly review schedule. Includes the complete vendor register. Maps to SOC 2 CC9.2.',
    templateContent: `# Vendor Management Policy & Register

**{{brandEntity}}**

Effective Date: {{effectiveDate}}
Policy Owner: {{ceoName}} (CEO)
Review Cycle: Quarterly

## 1. Purpose and Scope
{{AI_GENERATED_CONTENT}}

## 2. Vendor Register

| Vendor | Data Type | Agreement | SOC 2 / Equiv | BAA Required | BAA Status | Last Review |
|--------|-----------|-----------|---------------|--------------|------------|-------------|
| {{cloudProvider}} | PHI, PII, all data | GCP TOS | SOC 2 Type II | Yes | {{gcpBaaStatus}} | |
| Anthropic (Claude) | PHI via proxy | API TOS | SOC 2 Type II | Yes | {{anthropicBaaStatus}} | |
| Google (Gemini) | Non-PHI only | API TOS | SOC 2 Type II | No (no PHI) | N/A | |
| {{telehealthNetwork}} | PHI (telehealth) | Partner agreement | Verify | Yes | {{telehealthBaaStatus}} | |
| {{paymentProcessor}} | Payment data | Stripe TOS | PCI DSS + SOC 2 | No (no PHI) | N/A | |
| Pharmacy Partner | PHI (Rx fulfillment) | Partnership agreement | Verify | Yes | {{pharmacyBaaStatus}} | |

## 3. Vendor Assessment Criteria
{{AI_GENERATED_CONTENT}}

## 4. Vendor Onboarding Process
{{AI_GENERATED_CONTENT}}

## 5. Ongoing Monitoring
{{AI_GENERATED_CONTENT}}

## 6. BAA Management
{{AI_GENERATED_CONTENT}}

## 7. Vendor Risk Scoring
{{AI_GENERATED_CONTENT}}

## 8. Vendor Termination Process
{{AI_GENERATED_CONTENT}}

## 9. Quarterly Review Process
{{AI_GENERATED_CONTENT}}

Approved by: {{ceoName}}, CEO
Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'effectiveDate',
      'cloudProvider',
      'telehealthNetwork',
      'paymentProcessor',
      'complianceEmail',
    ],
    generationPrompt:
      `Generate a Vendor Management Policy & Register for {{brandEntity}} that maps to SOC 2 CC9.2.

Current vendor inventory:
1. {{cloudProvider}} (Google Cloud / Firebase): Hosts all data — PHI, PII, business data. Has SOC 2 Type II. BAA available via GCP console. Data type: all platform data.
2. Anthropic (Claude API): Processes PHI via Cloud Function proxy (API keys never in client). Has SOC 2 Type II. BAA template ready. Data type: PHI sent for AI analysis.
3. Google (Gemini API): Client-side AI for non-PHI tasks only (intake nurse, enrichment). Has SOC 2 Type II. No BAA needed (no PHI). Data type: non-PHI health queries.
4. {{telehealthNetwork}}: Provides telehealth consultations. Verify SOC 2 status. BAA template ready. Data type: PHI (patient consultations).
5. {{paymentProcessor}} (Stripe): Payment processing. Has PCI DSS + SOC 2. No BAA needed (no PHI touches Stripe). Data type: payment card data.
6. Pharmacy Partner (compounding pharmacy): Prescription fulfillment. Verify SOC 2 status. BAA template ready. Data type: PHI (prescriptions, patient info).

Assessment criteria for new vendors:
- Security posture: SOC 2 Type II report or equivalent (ISO 27001, HITRUST)
- Data handling: What data types will they access? PHI requires BAA.
- Encryption: At-rest and in-transit encryption standards
- Incident response: Documented breach notification procedures
- Business continuity: Uptime SLA and disaster recovery plan
- Regulatory compliance: HIPAA, state privacy laws as applicable

Vendor onboarding: Assessment → legal review → BAA execution (if PHI) → access provisioning → audit log entry → add to register.

Quarterly review: Check SOC 2 report currency, BAA expiration, service changes, incident history, SLA compliance. Document reviewer, date, findings, actions.

Vendor risk scoring: Critical (PHI access + infrastructure dependency), High (PHI access), Medium (PII only), Low (public data only).

Format: formal policy with complete vendor register table. Suitable for SOC 2 auditor review.`,
    qualityGate: VENDOR_MGMT_GATE,
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 10,
    sortOrder: 6,
    generationRound: 7,
    dependencies: ['5.1-baa-llc-pc'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 13.7 Monitoring & Alerting Policy ──
  {
    id: '13.7-monitoring-alerting-policy',
    categoryId: 'soc2-infosec',
    title: 'Monitoring & Alerting Policy',
    description:
      'Defines what systems are monitored, alert thresholds, escalation procedures, and dashboard requirements. Covers uptime, Cloud Function errors, AI API health, auth anomalies, payment failures, and doc gen quality. Maps to SOC 2 CC7.1-CC7.3.',
    templateContent: `# Monitoring & Alerting Policy

**{{brandEntity}}**

Effective Date: {{effectiveDate}}
Policy Owner: {{ceoName}} (CEO)
Review Cycle: Annual

## 1. Purpose and Scope
{{AI_GENERATED_CONTENT}}

## 2. Monitoring Matrix

| Monitor | What It Watches | Alert Condition | Alert Channel | Priority |
|---------|----------------|-----------------|---------------|----------|
| Uptime | HTTP response from frontend URLs | No 200 for 2 consecutive checks (10 min) | Email + Slack | Critical |
| Cloud Function Errors | Error rate across all Cloud Functions | Error rate > 5% in 15-minute window | GCP Monitoring → Email | Critical |
| AI API Health | Response time and error rate (Claude, Gemini) | > 10% error rate or > 30s avg response | Audit log alert → Email | High |
| Auth Anomaly | Failed login attempts per user per hour | > 5 failed logins from same IP in 1 hour | Audit log trigger → Email | High |
| Payment Failures | Stripe webhook delivery and payment success | Failure rate > 10% in 1-hour window | Stripe dashboard + Email | High |
| Doc Gen Quality | Quality gate scores from document generation | Any doc below threshold after auto-fix | Admin dashboard flag | Medium |

## 3. Monitoring Infrastructure
{{AI_GENERATED_CONTENT}}

## 4. Alert Escalation Procedures
{{AI_GENERATED_CONTENT}}

## 5. On-Call Responsibilities
{{AI_GENERATED_CONTENT}}

## 6. Dashboard Requirements
{{AI_GENERATED_CONTENT}}

## 7. Log Aggregation and Retention
{{AI_GENERATED_CONTENT}}

## 8. Performance Baselines
{{AI_GENERATED_CONTENT}}

## 9. Monthly Monitoring Review
{{AI_GENERATED_CONTENT}}

Approved by: {{ceoName}}, CEO
Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'effectiveDate',
      'complianceEmail',
    ],
    generationPrompt:
      `Generate a Monitoring & Alerting Policy for {{brandEntity}} that maps to SOC 2 CC7.1-CC7.3.

Monitoring stack:
1. UptimeRobot (free tier): HTTP uptime monitoring. Ping site every 5 min. Email/Slack on downtime.
2. Google Cloud Monitoring: Cloud Function errors, latency, invocation counts. Built into GCP.
3. Firebase Crashlytics: Client-side crash reporting. Captures React error boundaries.
4. Stripe webhook monitoring: Failed payments, webhook delivery failures. Built into Stripe dashboard.
5. Custom AI error rate tracking: Log AI API failures in Firestore audit_logs. Alert if error rate > 5% in 1 hour.
6. Custom auth anomaly detection: Monitor failed login attempts. Alert if > 5 failures from same IP in 1 hour.

Monitoring matrix (from platform spec):
- Uptime: No 200 response for 2 consecutive checks (10 min) → Critical
- Cloud Function Errors: Error rate > 5% in any 15-minute window → Critical
- AI API Health: Any AI provider > 10% error rate or > 30s avg response → High
- Auth Anomaly: > 5 failed logins from same IP in 1 hour → High
- Payment Failures: Payment failure rate > 10% in any 1-hour window → High
- Doc Gen Quality: Any document scores below threshold after auto-fix → Medium

Alert escalation:
- Critical: Immediate notification to {{ceoName}} + on-call engineer. 15-minute response SLA.
- High: Email notification. 1-hour response SLA during business hours.
- Medium: Dashboard flag. Next business day review.

Platform-level monitoring: Every brand gets monitored automatically. No per-brand configuration needed. A new brand URL gets uptime monitoring on launch.

Audit logging: All monitoring events logged to audit_logs collection. SYS_ERROR, SYS_WEBHOOK, SYS_RATE_LIMIT action codes.

Monthly review: Review alert history, false positive rate, response times, threshold appropriateness. Document reviewer, date, findings, threshold adjustments.

Format: formal policy document suitable for SOC 2 auditor review.`,
    qualityGate: MONITORING_GATE,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 8,
    sortOrder: 7,
    generationRound: 7,
    dependencies: ['8.7-incident-response-runbook'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 13.8 Risk Assessment Policy ──
  {
    id: '13.8-risk-assessment-policy',
    categoryId: 'soc2-infosec',
    title: 'Risk Assessment Policy',
    description:
      'Defines risk identification methodology, risk scoring criteria, risk register format, treatment options, and annual assessment schedule. Maps to SOC 2 CC3.1-CC3.4.',
    templateContent: `# Risk Assessment Policy

**{{brandEntity}}**

Effective Date: {{effectiveDate}}
Policy Owner: {{ceoName}} (CEO)
Review Cycle: Annual

## 1. Purpose and Scope
{{AI_GENERATED_CONTENT}}

## 2. Risk Assessment Methodology
{{AI_GENERATED_CONTENT}}

## 3. Risk Scoring Criteria

| Likelihood | Score | Description |
|-----------|-------|-------------|
| Rare | 1 | Less than once per year |
| Unlikely | 2 | Once per year |
| Possible | 3 | Several times per year |
| Likely | 4 | Monthly |
| Almost Certain | 5 | Weekly or more |

| Impact | Score | Description |
|--------|-------|-------------|
| Negligible | 1 | No data exposure, < 1 hour downtime |
| Minor | 2 | Non-PHI data exposure, < 4 hours downtime |
| Moderate | 3 | Limited PHI exposure (< 500 records), < 24 hours downtime |
| Major | 4 | Significant PHI exposure (500+ records), multi-day outage |
| Critical | 5 | Mass PHI breach, regulatory action, business continuity threat |

## 4. Risk Register
{{AI_GENERATED_CONTENT}}

## 5. Risk Treatment Options
{{AI_GENERATED_CONTENT}}

## 6. Risk Acceptance Criteria
{{AI_GENERATED_CONTENT}}

## 7. Annual Assessment Process
{{AI_GENERATED_CONTENT}}

## 8. Risk Reporting
{{AI_GENERATED_CONTENT}}

Approved by: {{ceoName}}, CEO
Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'effectiveDate',
      'complianceEmail',
    ],
    generationPrompt:
      `Generate a Risk Assessment Policy for {{brandEntity}} that maps to SOC 2 CC3.1-CC3.4.

Risk scoring: 5x5 matrix (Likelihood 1-5 × Impact 1-5). Risk score = L × I. Critical: 20-25. High: 12-19. Medium: 6-11. Low: 1-5.

Generate an initial risk register with these identified risks:
1. PHI data breach via unauthorized access (L:2 × I:5 = 10, Medium) — mitigated by Firestore rules, encryption, access controls
2. AI API provider outage (L:3 × I:3 = 9, Medium) — mitigated by graceful degradation, retry logic
3. Cloud infrastructure outage (L:1 × I:4 = 4, Low) — mitigated by {{cloudProvider}} 99.95% SLA, BC/DR plan
4. Insider threat / unauthorized admin access (L:2 × I:4 = 8, Medium) — mitigated by MFA, quarterly access reviews, audit logging
5. Third-party vendor breach (L:2 × I:4 = 8, Medium) — mitigated by BAAs, vendor assessments, quarterly reviews
6. Regulatory non-compliance (HIPAA/state law) (L:2 × I:5 = 10, Medium) — mitigated by compliance document library, quality gates, training
7. Payment fraud / Stripe compromise (L:2 × I:3 = 6, Medium) — mitigated by Stripe PCI compliance, webhook verification
8. AI model generating incorrect medical guidance (L:3 × I:4 = 12, High) — mitigated by quality gates, physician oversight, disclaimers
9. Consent management failure (L:2 × I:4 = 8, Medium) — mitigated by 7-purpose consent model, re-consent gates
10. Social engineering / phishing targeting admin credentials (L:3 × I:4 = 12, High) — mitigated by MFA, security awareness training

Risk treatment options: Mitigate (reduce likelihood or impact), Transfer (insurance, vendor), Accept (documented acceptance with rationale), Avoid (eliminate the activity).

Risk acceptance criteria: Risks scored Low (1-5) may be accepted with documentation. Medium (6-11) require active mitigation plan. High (12-19) require immediate mitigation. Critical (20-25) are unacceptable and require immediate action.

Annual assessment: Full risk register review, new risk identification, control effectiveness evaluation, treatment plan updates. Document assessor, date, findings, changes.

Format: formal policy document with risk register table. Suitable for SOC 2 auditor review.`,
    qualityGate: RISK_ASSESSMENT_GATE,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 10,
    sortOrder: 8,
    generationRound: 7,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 13.9 Acceptable Use Policy ──
  {
    id: '13.9-acceptable-use-policy',
    categoryId: 'soc2-infosec',
    title: 'Acceptable Use Policy',
    description:
      'Defines acceptable and prohibited use of company systems, devices, and data for all workforce members (employees, contractors, vendors). Covers internet use, email, device security, remote work, and PHI handling. Maps to SOC 2 CC1.1.',
    templateContent: `# Acceptable Use Policy

**{{brandEntity}}**

Effective Date: {{effectiveDate}}
Policy Owner: {{ceoName}} (CEO)
Review Cycle: Annual

## 1. Purpose and Scope
{{AI_GENERATED_CONTENT}}

## 2. Acceptable Use of Company Systems
{{AI_GENERATED_CONTENT}}

## 3. Prohibited Activities
{{AI_GENERATED_CONTENT}}

## 4. PHI Handling Requirements
{{AI_GENERATED_CONTENT}}

## 5. Device and Workstation Security
{{AI_GENERATED_CONTENT}}

## 6. Remote Work Security
{{AI_GENERATED_CONTENT}}

## 7. Email and Communication
{{AI_GENERATED_CONTENT}}

## 8. Password and Authentication
{{AI_GENERATED_CONTENT}}

## 9. Incident Reporting Obligations
{{AI_GENERATED_CONTENT}}

## 10. Consequences of Violations
{{AI_GENERATED_CONTENT}}

## Acknowledgment

I have read and understand the {{brandEntity}} Acceptable Use Policy. I agree to comply with all provisions.

**Signature:** ___________________________

**Printed Name:** ___________________________

**Date:** ___________________________

Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'effectiveDate',
      'complianceEmail',
    ],
    generationPrompt:
      `Generate an Acceptable Use Policy for {{brandEntity}} that maps to SOC 2 CC1.1.

Scope: All workforce members — employees, contractors, vendors with system access.

Acceptable use: Company systems used for business purposes. Incidental personal use permitted if it does not interfere with work, violate policy, or compromise security.

Prohibited activities:
- Accessing, storing, or transmitting PHI on personal devices without approval
- Sharing login credentials or MFA tokens
- Disabling or circumventing security controls (Firestore rules, MFA, encryption)
- Installing unauthorized software on company systems
- Using company systems for illegal activities
- Storing PHI in unapproved locations (personal email, cloud storage, USB drives)
- Sending PHI via unencrypted channels
- Accessing patient data without legitimate business need (minimum necessary principle)

PHI handling requirements: All PHI access must follow minimum necessary standard. PHI only accessed through authorized platform interfaces (admin dashboard, Cloud Functions). Never copied to local devices, personal email, or unauthorized cloud services. Report any suspected PHI exposure immediately to {{complianceEmail}}.

Device security: Screen lock after 5 minutes inactivity. Full disk encryption required. Antivirus/endpoint protection current. Automatic OS updates enabled.

Remote work: Use company-approved VPN when accessing admin systems. Secure home Wi-Fi (WPA2/WPA3). No public Wi-Fi for admin access. Physical privacy when viewing PHI.

Password policy: Minimum 12 characters, unique per service, password manager recommended. MFA required for admin, provider, and owner roles.

Incident reporting: All workforce members must report suspected security incidents, policy violations, suspicious activity, or PHI exposures within 4 hours of discovery.

Consequences: Verbal warning → written warning → access suspension → termination. Severity-dependent. Intentional PHI violations may result in immediate termination and regulatory reporting.

Format: formal policy with acknowledgment signature block. Suitable for SOC 2 auditor review and workforce distribution.`,
    qualityGate: AUP_GATE,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 6,
    sortOrder: 9,
    generationRound: 7,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 13.10 SOC 2 Readiness Checklist & Evidence Map ──
  {
    id: '13.10-soc2-evidence-map',
    categoryId: 'soc2-infosec',
    title: 'SOC 2 Readiness Checklist & Evidence Map',
    description:
      'Maps every SOC 2 Trust Service Criterion to the specific control, evidence artifact, responsible party, and current status. Serves as the master tracking document for SOC 2 audit preparation.',
    templateContent: `# SOC 2 Readiness Checklist & Evidence Map

**{{brandEntity}}**

Prepared: {{effectiveDate}}
Prepared By: {{ceoName}} (CEO)

## Trust Service Criteria: Security (CC — Required)

| Ref | Criterion | Control | Evidence | Owner | Status |
|-----|-----------|---------|----------|-------|--------|
| CC1.1 | Control environment | Information Security Policy, Acceptable Use Policy | Signed policies on file | CEO | |
| CC1.2 | Board oversight | Operating Agreement governance provisions | Operating Agreement §governance | CEO | |
| CC2.1 | Information communication | Compliance Training Schedule | Training completion records | Compliance | |
| CC3.1 | Risk assessment | Risk Assessment Policy, risk register | Annual risk assessment report | CEO | |
| CC3.2 | Fraud risk | Risk register (insider threat, social engineering) | Risk treatment plans | CEO | |
| CC3.3 | Change risk | Change Management Policy | CI/CD logs, PR history | Engineering | |
| CC3.4 | Risk monitoring | Monitoring & Alerting Policy | Monthly monitoring reviews | Engineering | |

{{AI_GENERATED_CONTENT}}

## Trust Service Criteria: Availability (A)
{{AI_GENERATED_CONTENT}}

## Trust Service Criteria: Processing Integrity (PI)
{{AI_GENERATED_CONTENT}}

## Trust Service Criteria: Confidentiality (C)
{{AI_GENERATED_CONTENT}}

## Trust Service Criteria: Privacy (P)
{{AI_GENERATED_CONTENT}}

## Evidence Collection Schedule
{{AI_GENERATED_CONTENT}}

## Pre-Audit Timeline
{{AI_GENERATED_CONTENT}}

Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'effectiveDate',
      'complianceEmail',
    ],
    generationPrompt:
      `Generate a SOC 2 Readiness Checklist & Evidence Map for {{brandEntity}}.

Map EVERY SOC 2 Trust Service Criterion to specific controls, evidence artifacts, responsible parties, and status. This is the master tracking document for SOC 2 Type II audit preparation.

SECURITY (CC — Required, CC1-CC9):
- CC1: Control environment → Information Security Policy, Acceptable Use Policy, org chart
- CC2: Communication → Compliance Training Schedule, policy distribution records
- CC3: Risk assessment → Risk Assessment Policy, risk register, annual assessment report
- CC4: Monitoring → Monitoring & Alerting Policy, alert logs, monthly reviews
- CC5: Control activities → All subordinate policies, Firestore rules, Cloud Function proxy
- CC6: Logical/physical access → Access Control Policy, MFA config, quarterly access reviews, Firestore security rules
- CC7: System operations → Monitoring & Alerting Policy, uptime logs, incident response records
- CC8: Change management → Change Management Policy, CI/CD logs, PR history, deploy logs
- CC9: Risk mitigation → Vendor Management Policy, BAA register, vendor assessment records

AVAILABILITY (A1):
- A1.1: System availability → BC/DR Plan, uptime SLA (Firebase 99.95%), UptimeRobot logs
- A1.2: Recovery objectives → RPO 1 hour (Firestore automatic), RTO 4 hours, annual DR test

PROCESSING INTEGRITY (PI1):
- PI1.1: Processing validation → Quality gate scoring, deterministic rubrics, auto-fix loop
- PI1.2: Input validation → Firestore rules, schema validation, error boundaries
- PI1.3: Error handling → Retry logic with backoff, error boundaries, Cloud Function error logging

CONFIDENTIALITY (C1):
- C1.1: Confidential data → Data Classification Policy (PHI/PII/Business Confidential/Public)
- C1.2: Data disposal → Data Retention Policy, automated deletion via Cloud Functions

PRIVACY (P1-P8):
- P1.1: Privacy notice → HIPAA NPP, Privacy Policy, Consumer Health Data Policy
- P2.1: Consent → 7-purpose consent model, granular opt-in, withdrawal mechanics
- P3.1: Collection → Consent Management Policy, minimum necessary standard
- P4.1: Use/retention → Data Retention Policy (6-year HIPAA minimum for PHI)
- P5.1: Access rights → Data subject access request process (30-day SLA)
- P6.1: Disclosure → Third-Party Data Sharing Policy, BAA requirements
- P7.1: Quality → Data validation, quality gates, brand accuracy scoring
- P8.1: Monitoring → Privacy impact assessments, consent rate tracking

Evidence artifacts for each criterion: specific document name, location (Firestore collection, GitHub repo, admin dashboard, Google Drive), and responsible party.

Total estimated engineering effort: 40-60 hours. Total estimated documentation effort: 20-30 hours. Timeline: spread over 6-12 months of normal development.

Evidence collection schedule: What evidence to collect monthly, quarterly, and annually.

Pre-audit timeline: 6 months before audit engagement — what to complete by each milestone.

Format: comprehensive evidence map with tables for each Trust Service Criteria category. Suitable for SOC 2 auditor review and internal tracking.`,
    qualityGate: SOC2_EVIDENCE_GATE,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 15,
    sortOrder: 10,
    generationRound: 7,
    dependencies: [
      '13.1-information-security-policy',
      '13.2-access-control-policy',
      '13.3-change-management-policy',
      '13.4-data-classification-policy',
      '13.5-encryption-key-management-policy',
      '13.6-vendor-management-policy',
      '13.7-monitoring-alerting-policy',
      '13.8-risk-assessment-policy',
      '13.9-acceptable-use-policy',
    ],
    anchorSections: [],
    inheritedProvisions: [],
  },
];
